import {isNilOrEmpty} from 'ramda-adjunct';

import {AuditData} from '../../../store/carAudit/reducerUtils';
import {AuditCategoriesWithError} from '../../../types/ConditionTypes';
import {areRequiredParamConditionsMet} from './areRequiredParamConditionsMet';
import {isValidValue} from './getFormSchema';
import {RequiredParam, RequiredParams} from './getRequiredFieldsFromCategories';

/**
 * Matches the mandatory params of the audit structure against the data the audit holds.
 * Every bucket of RequiredParams has its own storage on the audit, so each one has to be
 * validated against the matching slice: fields, assets and videoAssets.
 */
export const getCategoriesWithError = (
  audit: Pick<AuditData, 'fields' | 'assets' | 'videoAssets'>,
  requiredFormFields?: RequiredParams
): AuditCategoriesWithError => {
  if (!requiredFormFields) {
    return {};
  }

  const categoriesWithError: AuditCategoriesWithError = {};

  const flagCategories = ({categoryIds}: RequiredParam) =>
    categoryIds.forEach((categoryId) => {
      categoriesWithError[categoryId] = true;
    });

  const getParamsToValidate = (bucket: Record<string, RequiredParam>) =>
    Object.entries(bucket).filter(([, requiredParam]) =>
      areRequiredParamConditionsMet(audit?.fields, requiredParam)
    );

  getParamsToValidate(requiredFormFields.fields).forEach(([key, requiredParam]) => {
    const field = audit?.fields?.[key];
    if (!field || !isValidValue(field)) {
      flagCategories(requiredParam);
    }
  });

  getParamsToValidate(requiredFormFields.assets).forEach(([key, requiredParam]) => {
    if (!audit?.assets?.[key]) {
      flagCategories(requiredParam);
    }
  });

  // An existing but empty videoAssets record means no video was uploaded, so it must not pass
  getParamsToValidate(requiredFormFields.videos).forEach(([key, requiredParam]) => {
    if (isNilOrEmpty(audit?.videoAssets?.[key])) {
      flagCategories(requiredParam);
    }
  });

  return categoriesWithError;
};
